/**
 * PostHog custom event tracking for the student learning funnel.
 * Safe to call before PostHog loads — events are queued and flushed once PostHog is ready.
 */

const pendingEvents = [];

function capture(event, properties = {}) {
  if (window.posthog) {
    window.posthog.capture(event, properties);
  } else {
    pendingEvents.push({ event, properties });
  }
}

export function flushPendingEvents() {
  if (!window.posthog) return;
  while (pendingEvents.length > 0) {
    const { event, properties } = pendingEvents.shift();
    window.posthog.capture(event, properties);
  }
}

export function trackDashboardViewed({ studentId, assignmentCount, completedCount, todoCount }) {
  capture('dashboard_viewed', {
    student_id: studentId,
    assignment_count: assignmentCount,
    completed_count: completedCount,
    todo_count: todoCount,
  });
}

// ── Library (self-study /library cohort) ──────────────────────────────────
// /library is the homepage for adult self-study customers. These light up the
// activation funnel (view → open course → play lesson) + content/engagement for
// that cohort. customer_type rides along as a PostHog person property (set at
// identify), so insights can filter to self_study without a per-event field.

export function trackLibraryViewed({ courseCount, entitledCourseCount }) {
  capture('library_viewed', {
    course_count: courseCount,
    entitled_course_count: entitledCourseCount,
  });
}

export function trackLibraryCourseOpened({ courseCode }) {
  capture('library_course_opened', { course_code: courseCode });
}

/** /library 探索: a course page for sale opened. */
export function trackLibraryExploreOpened({ sku }) {
  capture('library_explore_opened', { sku });
}

/** /library 去购买: off to the 微信小店 (hasStoreUrl: the product's own page vs the shop). */
export function trackLibraryBuyClicked({ sku, hasStoreUrl }) {
  capture('library_buy_clicked', { sku, has_store_url: hasStoreUrl });
}

export function trackLibraryLessonPlayed({ courseCode, book, lessonNumber }) {
  capture('library_lesson_played', {
    course_code: courseCode,
    book,
    lesson_number: lessonNumber,
  });
}

export function trackLibraryContactClicked() {
  capture('library_contact_clicked', {});
}

export function trackLessonStarted({ studentId, course, book, lessonNumber, source, assignmentId }) {
  capture('lesson_started', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    source,
    assignment_id: assignmentId,
  });
}

export function trackLessonCompleted({ studentId, course, book, lessonNumber, assignmentId, durationSeconds }) {
  capture('lesson_completed', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    duration_seconds: durationSeconds,
  });
}

export function trackLessonExited({ studentId, course, book, lessonNumber, assignmentId, completedStepsCount, durationSeconds }) {
  capture('lesson_exited', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    completed_steps_count: completedStepsCount,
    duration_seconds: durationSeconds,
  });
}

// Main lesson video — fires once per mount on first 'play'. If the kid never
// hits play, nothing fires (lesson_started already covers entry).
export function trackLessonVideoStarted({ studentId, course, book, lessonNumber, assignmentId }) {
  capture('lesson_video_started', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
  });
}

export function trackLessonVideoCompleted({ studentId, course, book, lessonNumber, assignmentId, secondsWatched, totalDurationSeconds }) {
  capture('lesson_video_completed', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    seconds_watched: secondsWatched,
    total_duration_seconds: totalDurationSeconds,
  });
}

// Fires on unmount/src-change AFTER lesson_video_started fired but BEFORE
// lesson_video_completed. Pairs with started on a 1:1 basis when video isn't
// played to the end.
export function trackLessonVideoExited({ studentId, course, book, lessonNumber, assignmentId, secondsWatched, totalDurationSeconds }) {
  capture('lesson_video_exited', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    seconds_watched: secondsWatched,
    total_duration_seconds: totalDurationSeconds,
  });
}

// --- Selina英语启蒙 (weekly plan) events ---
// Low-frequency: one per page open / plan action / finished quiz. No per-tap events.

export function trackSelinaWeekViewed({ studentId, week, theme, dayOfWeek, paused }) {
  capture('selina_week_viewed', {
    student_id: studentId, week, theme, day_of_week: dayOfWeek, paused,
  });
}

export function trackSelinaModeOpened({ studentId, week, mode }) {
  capture('selina_mode_opened', { student_id: studentId, week, mode });
}

/** Read With Selina preview: a non-owner tapped 加小助手微信 to get it opened. */
export function trackRwsContactClicked() {
  capture('rws_contact_clicked', {});
}

/** /kidsSongs → /library: the 首页 button in the song list header. */
/** The one-time "宝宝几岁啦？" ask: shown / saved / later — how many parents answer. */
export function trackBirthYmPrompt({ surface, action }) {
  capture('birth_ym_prompt', { surface, action });
}

/**
 * 放到桌面 popup (kids songs): `shown` (auto = Safari on its own, fullscreen = the ⛶ hint),
 * then what the parent did — `accepted` (好，我来添加), `guide` (看图文步骤) or `dismissed`. A handful a day at most.
 */
export function trackSelinaInstallNudge({ action, trigger, clip }) {
  capture('selina_install_nudge', { action, trigger, clip });
}

/**
 * A kids-songs quiz question whose pictures are not on screen 3 s in (usePicturesCheck): per picture
 * whether it downloaded, its drawn and box sizes, and its display/visibility/opacity. Once per page,
 * only on a failure — Huawei Browser 17.9 showed empty cards (Rob, 2026-10-21).
 */
export function trackSelinaQuizPicturesMissing({ missing, pictures, viewport, standalone }) {
  capture('selina_quiz_pictures_missing', { missing, pictures, viewport, standalone });
}

export function trackSelinaLibraryOpened() {
  capture('selina_library_opened', {});
}

export function trackSelinaPlanAction({ studentId, action, fromWeek, toWeek }) {
  capture('selina_plan_action', { student_id: studentId, action, from_week: fromWeek, to_week: toWeek });
}

export function trackSelinaTestCompleted({ studentId, week, known, total }) {
  capture('selina_test_completed', { student_id: studentId, week, known, total });
}

// Per-song listening on the weekly plan. Two events per song at most — see
// useSongPlayTracking for why this folds song_video_*'s completed/exited into
// one `reason`, and why the seconds come from the media element rather than a
// stopwatch. ~30 events per 30-minute sitting.
// A video/audio play() that the browser REFUSED. Every play() on the song page used to end
// in `.catch(() => {})`, so when parents reported "the play button gives errors" there was
// nothing in PostHog to look at and nothing in the console either — the aggregate play counts
// looked healthy because the plays that worked still fired. The name is what matters:
// NotAllowedError is a missing user gesture (autoplay policy), NotSupportedError is the source
// or codec, AbortError is a second load interrupting the first.
export function trackSelinaPlayFailed({ songTitle, surface, userInitiated, errorName, errorMessage }) {
  capture('selina_play_failed', {
    song_title: songTitle,
    surface,
    user_initiated: userInitiated,
    error_name: errorName || 'unknown',
    error_message_snippet: (errorMessage || '').slice(0, 200),
  });
}

export function trackSelinaSongPlayed({ studentId, week, songKey, songTitle, surface, player, engine }) {
  capture('selina_song_played', {
    // engine: hlsjs / native (the phone's own HLS) / file — which code is decoding it
    student_id: studentId, week, song_key: songKey, song_title: songTitle, surface, player, engine: engine ?? null,
  });
}

/** The previous page died without closing (see utils/pageLifecycle.js). */
export function trackPageEndedUnexpectedly(props) {
  capture('page_ended_unexpectedly', props);
}

export function trackSelinaSongFinished({ studentId, week, songKey, songTitle, surface, player, secondsPlayed, durationSeconds, completed, reason }) {
  capture('selina_song_finished', {
    student_id: studentId,
    week,
    song_key: songKey,
    song_title: songTitle,
    surface,
    player,
    seconds_played: secondsPlayed,
    duration_seconds: durationSeconds,
    completed,
    reason,
    // How much of the song reached the room, for "started but abandoned" queries.
    percent_played: durationSeconds ? Math.round((secondsPlayed / durationSeconds) * 100) : null,
  });
}

// --- Song Gallery events ---
// Fired by /songs/gallery kid-mode player. Separate from lesson_video_* because
// gallery browses are not homework — no backend persistence, no completion semantics.
// Pattern mirrors lesson_video_*: started fires once per (song, video_type) mount,
// completed fires on 'ended', exited fires on unmount/src-change if started but not completed.

export function trackSongVideoStarted({ studentId, songSeq, songTitle, videoType }) {
  capture('song_video_started', {
    student_id: studentId,
    song_seq: songSeq,
    song_title: songTitle,
    video_type: videoType,
  });
}

export function trackSongVideoCompleted({ studentId, songSeq, songTitle, videoType, secondsWatched, totalDurationSeconds }) {
  capture('song_video_completed', {
    student_id: studentId,
    song_seq: songSeq,
    song_title: songTitle,
    video_type: videoType,
    seconds_watched: secondsWatched,
    total_duration_seconds: totalDurationSeconds,
  });
}

export function trackSongVideoExited({ studentId, songSeq, songTitle, videoType, secondsWatched, totalDurationSeconds }) {
  capture('song_video_exited', {
    student_id: studentId,
    song_seq: songSeq,
    song_title: songTitle,
    video_type: videoType,
    seconds_watched: secondsWatched,
    total_duration_seconds: totalDurationSeconds,
  });
}

export function trackSongVideoToggled({ studentId, songSeq, fromVideoType, toVideoType }) {
  capture('song_video_toggled', {
    student_id: studentId,
    song_seq: songSeq,
    from_video_type: fromVideoType,
    to_video_type: toVideoType,
  });
}

// `method` is 'arrow' | 'swipe' | 'auto_advance' — separates kid-driven nav
// from auto-advance-on-end so we can measure how often kids navigate vs let songs play.
export function trackSongNavigated({ studentId, fromSeq, toSeq, method }) {
  capture('song_navigated', {
    student_id: studentId,
    from_seq: fromSeq,
    to_seq: toSeq,
    method,
  });
}

// Denominator for the recording funnel (paired with recording_uploaded and
// recording_failed). recordingType is optional because the low-level recorder
// hook is type-agnostic — callers that know the type can pass it.
export function trackRecordingAttempted({ recordingType = null } = {}) {
  capture('recording_attempted', {
    recording_type: recordingType,
  });
}

// Captures engagement (full listens + raw click count) at the moment a kid
// commits to recording. Pairs click-count with completed-listens so the gap
// between them quantifies rage-clicking after the listen-gate was removed.
export function trackRecordingEngagementSignal({
  course,
  book,
  lessonNumber,
  itemId,
  itemIndex,
  completedListens,
  clickCount,
  isRerecord,
}) {
  capture('recording_engagement_signal', {
    course,
    book,
    lesson_number: lessonNumber,
    item_id: itemId,
    item_index: itemIndex,
    completed_listens: completedListens,
    click_count: clickCount,
    listened_first: completedListens > 0,
    is_rerecord: isRerecord,
  });
}

export function trackRecordingUploaded({ studentId, course, book, lessonNumber, assignmentId, recordingType }) {
  capture('recording_uploaded', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    recording_type: recordingType,
  });
}

// `source` distinguishes pre-warm (returning user) from fresh getUserMedia
// (might prompt). `permission_state_before` is the navigator.permissions state
// captured BEFORE the call — combined with `source`, lets us detect the bug
// "fresh getUserMedia fired when permission was already granted but pre-warm
// didn't run." Defaults preserve backward compat with any existing caller.
export function trackMicPermissionGranted({ source = 'unknown', permissionStateBefore = null } = {}) {
  capture('mic_permission_granted', {
    source,
    permission_state_before: permissionStateBefore,
  });
}

export function trackMicPermissionDenied({ source = 'unknown', permissionStateBefore = null, errorName }) {
  capture('mic_permission_denied', {
    source,
    permission_state_before: permissionStateBefore,
    error_name: errorName,
  });
}

export function trackRecordingFailed({ studentId, course, book, lessonNumber, assignmentId, errorCode, recordingType }) {
  capture('recording_failed', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    assignment_id: assignmentId,
    error_code: errorCode,
    recording_type: recordingType,
  });
}

// A take the recorder threw away before upload: the mic returned no audio
// (iOS mutes an idle pre-warmed stream when the page plays other audio) or the
// blob was under the size floor. Pairs with mic_permission_granted so the gap
// before recording_attempted is explained instead of silent.
export function trackRecordingDiscarded({ reason, bytes = 0, durationMs = null, micSource = null }) {
  capture('recording_discarded', {
    reason,
    bytes,
    duration_ms: durationMs,
    mic_source: micSource,
  });
}

export function trackLessonLoadFailed({ course, book, lessonNumber, reason }) {
  capture('lesson_load_failed', {
    course,
    book,
    lesson_number: lessonNumber,
    reason,
  });
}

export function trackSubmissionReviewed({ teacherId, studentId, submissionId, course, lessonNumber, rating, latencyHours, listenCount }) {
  capture('submission_reviewed', {
    teacher_id: teacherId,
    student_id: studentId,
    submission_id: submissionId,
    course,
    lesson_number: lessonNumber,
    rating,
    latency_hours: latencyHours,
    listen_count: listenCount,
  });
}

export function trackTeacherFeedbackAudioPlayed({ submissionId, studentId, course, book, lessonNumber, source, mode }) {
  capture('teacher_feedback_audio_played', {
    submission_id: submissionId,
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    source,
    mode,
  });
}

export function trackTeacherAudioFeedbackUploaded({ submissionId, studentId }) {
  capture('teacher_audio_feedback_uploaded', {
    submission_id: submissionId,
    student_id: studentId,
  });
}

export function trackTeacherFeedbackGateSkipped({ studentId, submissionId, itemId, course, book, lessonNumber }) {
  capture('teacher_feedback_gate_skipped', {
    student_id: studentId,
    submission_id: submissionId,
    item_id: itemId,
    course,
    book,
    lesson_number: lessonNumber,
  });
}

// Fires when a student bypasses the auto-extra 3-listen gate via the 10s
// escape hatch. Pairs with `recording_engagement_signal` (which sees
// completed_listens >= 3 for kids who cleared the gate legitimately) to
// distinguish bypass-driven re-records from listen-driven re-records on
// auto-E items.
export function trackAutoExtraListenGateSkipped({ itemId, course, book, lessonNumber, listenCount }) {
  capture('auto_extra_listen_gate_skipped', {
    item_id: itemId,
    course,
    book,
    lesson_number: lessonNumber,
    listen_count: listenCount,
  });
}

export function trackTeacherTextFeedbackSaved({ submissionId, studentId, length }) {
  capture('teacher_text_feedback_saved', {
    submission_id: submissionId,
    student_id: studentId,
    length,
  });
}

export function trackTeacherSmartFeedbackSaved({ submissionId, studentId, itemCount }) {
  capture('teacher_smart_feedback_saved', {
    submission_id: submissionId,
    student_id: studentId,
    item_count: itemCount,
  });
}

// --- Conversion funnel events ---

export function trackSignupCompleted({ userId, source }) {
  capture('signup_completed', {
    user_id: userId,
    utm_source: source,
  });
}

export function trackStarterRedeemed({ productName, courseCodes }) {
  capture('starter_redeemed', {
    product_name: productName,
    course_codes: courseCodes,
  });
}

export function trackRedemptionFailed({ code, error }) {
  capture('redemption_failed', {
    code_prefix: code?.slice(0, 4),
    error,
  });
}

export function trackVipEnrolled({ classId, className, studentId }) {
  capture('vip_enrolled', {
    class_id: classId,
    class_name: className,
    student_id: studentId,
  });
}

export function trackVipUnenrolled({ classId, className, studentId }) {
  capture('vip_unenrolled', {
    class_id: classId,
    class_name: className,
    student_id: studentId,
  });
}

export function trackEntitlementGranted({ productId, productSku, productName, studentId, source }) {
  capture('entitlement_granted', {
    product_id: productId,
    product_sku: productSku,
    product_name: productName,
    student_id: studentId,
    source,
  });
}

// --- Paywall events ---

export function trackPaywallShown({ courseCode, environment }) {
  capture('paywall_shown', {
    course_code: courseCode,
    environment,
  });
}

export function trackPaywallDismissed({ courseCode }) {
  capture('paywall_dismissed', {
    course_code: courseCode,
  });
}

export function trackPaywallCtaClicked({ courseCode, environment }) {
  capture('paywall_cta_clicked', {
    course_code: courseCode,
    environment,
  });
}

// --- Observability events ---

export function trackApiRequestFailed({
  endpoint,
  method,
  statusCode,
  durationMs,
  serverDurationMs = null,
  errorType,
  pagePath = null,
  pageLoadId = null,
}) {
  capture('api_request_failed', {
    endpoint,
    method,
    status_code: statusCode,
    duration_ms: durationMs,
    server_duration_ms: serverDurationMs,
    error_type: errorType,
    page_path: pagePath,
    page_load_id: pageLoadId,
  });
}

export function trackApiRequestCompleted({
  endpoint,
  method,
  statusCode,
  durationMs,
  serverDurationMs = null,
  activeRequests = null,
  loopLagMs = null,
  pagePath = null,
  pageLoadId = null,
}) {
  capture('api_request_completed', {
    endpoint,
    method,
    status_code: statusCode,
    duration_ms: durationMs,
    server_duration_ms: serverDurationMs,
    active_requests_at_arrival: activeRequests,
    loop_lag_ms: loopLagMs,
    page_path: pagePath,
    page_load_id: pageLoadId,
  });
}

export function trackChunkLoadFailed({ module, message }) {
  capture('chunk_load_failed', {
    module,
    message,
  });
}

export function trackVersionMismatch({ clientVersion, serverVersion }) {
  capture('version_mismatch', {
    client_version: clientVersion,
    server_version: serverVersion,
  });
}

export function trackServerHealthPing({ memoryRssMb, heapUsedMb, heapTotalMb, cpuLoad1m, freeMemMb, totalMemMb, uptimeSeconds }) {
  capture('server_health_ping', {
    memory_rss_mb: memoryRssMb,
    heap_used_mb: heapUsedMb,
    heap_total_mb: heapTotalMb,
    cpu_load_1m: cpuLoad1m,
    free_mem_mb: freeMemMb,
    total_mem_mb: totalMemMb,
    uptime_seconds: uptimeSeconds,
  });
}

export function trackAppSessionStarted({ role }) {
  capture('app_session_started', { role });
}

// Main-thread blocking tasks >300ms captured via PerformanceObserver('longtask').
// Paired with trackSlowInteraction below to diagnose INP spikes.
export function trackLongTask({ page, durationMs, startTime, name }) {
  capture('long_task_detected', {
    page,
    duration_ms: durationMs,
    start_time_ms: startTime,
    name,
  });
}

// Event Timing API entries slower than 200ms (the "needs improvement" INP
// threshold). Target descriptor tells us which element was tapped.
export function trackSlowInteraction({ page, eventType, durationMs, processingDurationMs, inputDelayMs, target }) {
  capture('slow_interaction', {
    page,
    event_type: eventType,
    duration_ms: durationMs,
    processing_duration_ms: processingDurationMs,
    input_delay_ms: inputDelayMs,
    target_tag: target?.tag || null,
    target_id: target?.id || null,
    target_testid: target?.data_testid || null,
  });
}

// --- Gamification events ---

export function trackStreakUpdated({ studentId, streakDays }) {
  capture('streak_updated', {
    student_id: studentId,
    streak_days: streakDays,
  });
}

export function trackStreakBroken({ studentId, previousStreakDays }) {
  capture('streak_broken', {
    student_id: studentId,
    previous_streak_days: previousStreakDays,
  });
}

export function trackLevelUp({ studentId, newLevelName, previousLevelName }) {
  capture('level_up', {
    student_id: studentId,
    new_level_name: newLevelName,
    previous_level_name: previousLevelName,
  });
}

export function trackQuizCompleted({ studentId, quizType, totalQuestions, correctCount, xpEarned, course }) {
  capture('quiz_completed', {
    student_id: studentId,
    quiz_type: quizType,
    total_questions: totalQuestions,
    correct_count: correctCount,
    xp_earned: xpEarned,
    course,
  });
}

// Fires on every /api/bootstrap round-trip (fast path only — opt-out callers
// hit /me instead). Queues pre-PostHog-load, which matters because bootstrap
// resolves during the same cold-boot window where PostHog is still lazy-loading.
// status=0 is the network-error bucket (timeout/DNS/TLS), distinct from HTTP errors.
export function trackBootstrapFetch({ status, durationMs, studentId, payloadSize }) {
  capture('bootstrap_fetch', {
    status,
    duration_ms: durationMs,
    student_id: studentId,
    payload_size: payloadSize,
  });
}

// --- Auth observability events ---
// Paired with the backend `auth_events` table. Frontend captures the side
// of the auth flow that the server can't see (login button click, callback
// arrival, redirect-to-login firing).

// Fires immediately before window.location.href = '/' on a 401 response.
// `reason` matches the backend `auth_401_emitted` reason taxonomy.
export function trackAuthRedirectToLogin({ reason, path, timeSincePageLoadMs }) {
  capture('auth_redirect_to_login', {
    reason,
    path,
    time_since_page_load_ms: timeSincePageLoadMs,
  });
}

// All five funnel events accept `correlationId` — a UUID generated in
// signIn() and stashed in sessionStorage so the callback handler can read it
// back. PostHog's funnel tile can't traverse anonymous→identified user
// transitions (the SDK rotates distinct_id when posthog.identify fires after
// session_established), so we correlate on a property instead of distinct_id.

// User clicked sign-in (signIn() called, before Authing redirect).
export function trackLoginInitiated({ redirectUrl, correlationId }) {
  capture('login_initiated', {
    redirect_url: redirectUrl,
    login_correlation_id: correlationId,
  });
}

// Authing redirected back with `?code=...`. Fires once per callback.
// statePrefix is first 8 chars (debugging mismatched-state issues).
export function trackLoginCallbackReceived({ hasPkce, statePrefix, correlationId }) {
  capture('login_callback_received', {
    has_pkce: hasPkce,
    state_prefix: statePrefix,
    login_correlation_id: correlationId,
  });
}

// Backend exchangeAuthCode succeeded. durationMs covers the BFF round-trip.
export function trackLoginCodeExchangeSuccess({ durationMs, correlationId }) {
  capture('login_code_exchange_success', {
    duration_ms: durationMs,
    login_correlation_id: correlationId,
  });
}

export function trackLoginCodeExchangeFailed({ errorName, errorMessageSnippet, errorStatus, errorBody, correlationId }) {
  capture('login_code_exchange_failed', {
    error_name: errorName,
    error_message_snippet: errorMessageSnippet,
    error_status: errorStatus,
    error_body: errorBody,
    login_correlation_id: correlationId,
  });
}

// fetchAndSetupUser resolved → user data populated. totalLoginMs spans
// signIn() → here, including Authing redirect + code exchange + /me fetch.
export function trackLoginSessionEstablished({ totalLoginMs, correlationId }) {
  capture('login_session_established', {
    total_login_ms: totalLoginMs,
    login_correlation_id: correlationId,
  });
}

// Sampled at 1% in sessionAuth — gives PostHog visibility into per-phase
// middleware latency without burning the event budget. Server-Timing header
// always carries the same data live for DevTools inspection.
export function trackSessionAuthTimingSampled({ cookieParseMs, dbLookupMs, refreshCheckMs, refreshCallMs, totalMs }) {
  capture('sessionauth_middleware_timing', {
    cookie_parse_ms: cookieParseMs,
    db_lookup_ms: dbLookupMs,
    refresh_check_ms: refreshCheckMs,
    refresh_call_ms: refreshCallMs,
    total_ms: totalMs,
  });
}

// --- Learning-plan funnel (public /roadmap lead-gen) ---
// Never send the raw contact value (PII) — only the contact method.

export function trackLearningPlanFunnelStarted({ utmSource } = {}) {
  capture('learning_plan_funnel_started', { utm_source: utmSource });
}

export function trackLearningPlanStepCompleted({ stepId, stepIndex, value }) {
  capture('learning_plan_step_completed', {
    step_id: stepId,
    step_index: stepIndex,
    value,
  });
}

export function trackLearningPlanContactSubmitted({ contactMethod, recommendedCourses }) {
  capture('learning_plan_contact_submitted', {
    contact_method: contactMethod,
    recommended_courses: recommendedCourses,
  });
}

export function trackLearningPlanCompleted({ age, level, goal, recommendedCourses }) {
  capture('learning_plan_completed', {
    child_age: age,
    english_level: level,
    learning_goal: goal,
    recommended_courses: recommendedCourses,
  });
}

export function trackLearningPlanCtaClicked({ target, courseId }) {
  capture('learning_plan_cta_clicked', { target, course_id: courseId });
}

// ---------------------------------------------------------------------------
// AI tutor
//
// The tab shipped with no telemetry, and the first question asked of it —
// "two flagged students did these exact lessons and produced zero attempts;
// did they never see the tab, never tap it, or could their browser not run
// it?" — was unanswerable. These five events exist to separate those cases.
//
// Volume is small by construction: one `shown` per lesson view that has tutor
// content, and the rest only fire on a tab a child actually opened.
// ---------------------------------------------------------------------------

/**
 * The tab was rendered. Fires whether or not the child ever touches it, which
 * is what makes `tab_opened` below meaningful as a ratio.
 *
 * Deliberately carries no question count: the tab is drawn from a static
 * manifest of which lessons HAVE content, and the count only exists after the
 * API call the child triggers by opening it.
 */
export function trackAiTutorTabShown({ studentId, course, book, lessonNumber }) {
  capture('ai_tutor_tab_shown', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
  });
}

/**
 * The child opened the tab — the signal that separates "never noticed it" from
 * "looked at it and did nothing", which call for completely different fixes.
 */
export function trackAiTutorTabOpened({ studentId, course, book, lessonNumber }) {
  capture('ai_tutor_tab_opened', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
  });
}

/**
 * This browser cannot record or encode audio, so the child sees a notice and
 * can do nothing else. No attempt row is ever written on this path, which is
 * exactly why it needs an event: server-side it is indistinguishable from a
 * child who ignored the tab.
 */
export function trackAiTutorUnsupported({ studentId, course, book, lessonNumber }) {
  capture('ai_tutor_unsupported', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
  });
}

/**
 * One teacher dictation in the assignment chat (AI 布置), however it ended.
 *
 * `outcome` is the whole story: 'text' (words landed in the box), 'silent'
 * (recogniser heard nothing), 'mic_denied', 'unsupported' (browser cannot
 * record or convert), 'unavailable' (recogniser off or down — our side),
 * 'failed' (anything else). No transcript rides along: it names children.
 * `chars` is the transcript length, which says "one word" vs "a sentence".
 */
export function trackAssignmentChatDictation({ classId, outcome, durationMs, chars }) {
  capture('assignment_chat_dictation', {
    class_id: classId,
    outcome,
    duration_ms: durationMs,
    chars,
  });
}

/** The child tapped the mic and refused (or had already refused) permission. */
export function trackAiTutorMicDenied({ studentId, course, book, lessonNumber }) {
  capture('ai_tutor_mic_denied', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
  });
}

/**
 * One graded answer.
 *
 * `recognised` replaced the old `has_audio`: audio is no longer optional (it is
 * the answer), so that property was true on every event and measured nothing.
 * What can still fail is turning it into words.
 */
export function trackAiTutorAnswerSubmitted({
  studentId, course, book, lessonNumber, questionId, attemptNumber, correct, recognised,
}) {
  capture('ai_tutor_answer_submitted', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    question_id: questionId,
    attempt_number: attemptNumber,
    correct,
    // No graded_by here on purpose: the answer response does not carry it, and
    // a property that is always null is worse than an absent one. The
    // deterministic-vs-llm split lives in ai_tutor_attempts.graded_by, which is
    // where the cost question is actually answered.
    recognised,
  });
}

/**
 * The server could not turn the child's recording into words.
 *
 * This event exists because of how the tutor's first outage was found. Speech
 * recognition used to run in the browser against Google's servers, which are
 * blocked in China; the first real student to tap the microphone got a network
 * error, no attempt row was written, and no event fired. From every product
 * signal we had, a completely broken feature and a feature nobody had tried
 * looked identical, and it took reading a single Sentry exception to tell them
 * apart. A recogniser that fails now says so in the same place we look to ask
 * whether anyone is using this at all.
 */
export function trackAiTutorRecogniserFailed({
  studentId, course, book, lessonNumber, questionId,
}) {
  capture('ai_tutor_recogniser_failed', {
    student_id: studentId,
    course,
    book,
    lesson_number: lessonNumber,
    question_id: questionId,
  });
}

/**
 * One graded 每日口语 answer. At most six a day per child (three prompts, two
 * tries), so well inside the event budget. `verdict` 'asr_failed' is the
 * recogniser failing, not the child — kept in the same event so "is speaking
 * broken" and "is anyone speaking" are one query.
 *
 * @param {{ level: number, verdict: 'pass'|'miss'|'asr_failed', attempt: number }} props
 *   `attempt` is attempts spent after this answer (an asr_failed spends none).
 */
export function trackDailySpeakingAnswered({ level, verdict, attempt }) {
  capture('daily_speaking_answered', { level, verdict, attempt });
}
